import type { EntitySelectControlSchema } from '@web-config-app/entity-form';
import type { EntityDetail } from '@web-config-app/core';
import { get } from 'lodash-es';

/**
 * Processes RSQL query strings, replacing template placeholders with actual entity data values.
 * Supports {{.}} shorthand to reference the same property path as the left side of the comparison.
 * Returns null if any placeholder cannot be resolved to prevent invalid queries from reaching the backend.
 */
export const getDomainEntityAttributesQueryProps = (
  entityRef: EntitySelectControlSchema['x-entity-reference'] | undefined,
  entityData: Partial<EntityDetail> | undefined,
) => {
  if (!entityRef) return null;

  const { domainEntityAttributesQuery } = entityRef;
  if (!domainEntityAttributesQuery) return null;

  // Matches patterns like: propertyPath=={{placeholder}}
  // Captures: (1) the property path, (2) the placeholder content
  const queryRegExp = /([\w.]+)=={{(.*?)}}/g;

  // Check if there are no placeholders to replace, return query as-is
  if (!queryRegExp.test(domainEntityAttributesQuery)) {
    return domainEntityAttributesQuery;
  }

  // Reset regex lastIndex after test() call
  queryRegExp.lastIndex = 0;

  // Track if any placeholder failed to resolve
  let hasUnresolvedPlaceholder = false;

  // Replace placeholders with actual values from entityData
  const processedQuery = domainEntityAttributesQuery.replace(
    queryRegExp,
    (match, propertyPath, placeholder) => {
      const trimmedPlaceholder = placeholder.trim();

      // If placeholder is ".", use the same path as the left side of the comparison
      const pathToResolve =
        trimmedPlaceholder === '.' ? propertyPath : trimmedPlaceholder;

      const value = get(
        entityData,
        `attributes.domainEntityAttributes.${pathToResolve}`,
      );

      // If value can't be resolved, mark as failed and keep placeholder
      if (value === undefined || value === null) {
        hasUnresolvedPlaceholder = true;
        return match;
      }

      return `${encodeURIComponent(propertyPath)}==${encodeURIComponent(
        value,
      )}`;
    },
  );

  // Return null if any placeholder couldn't be resolved
  if (hasUnresolvedPlaceholder) {
    return null;
  }

  return processedQuery;
};
